/**
 * CashbackContent - Configurações do programa de cashback
 * Extraído VERBATIM do App.js (Fase 2 da refatoração — zero mudança de comportamento).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const CashbackContent = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    enabled: false,
    percentage: 5.0,
    expiry_days: 30,
    minimum_order_value: 0.0
  });

  useEffect(() => {
    loadCashbackSettings();
  }, []);

  const loadCashbackSettings = async () => {
    try {
      const response = await axios.get(`${API}/cashback/settings`);
      setSettings(response.data);
    } catch (err) {
      console.error('Error loading cashback settings:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/cashback/settings`, settings);
      setSuccess('Configurações de cashback salvas com sucesso!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao salvar configurações de cashback');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Configurações de Cashback</h2>
          <p className="text-gray-400">Gerencie o programa de cashback para seus clientes</p>
        </div>
      </div>

      {/* Alerts */}
      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="text-green-500" size={20} />
          <span className="text-green-400">{success}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
          <XCircle className="text-red-500" size={20} />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Settings Card */}
      <div className="glassmorphism-card rounded-2xl p-8 space-y-6">
        {/* Enable Cashback Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-xl">
          <div className="flex-1">
            <Label htmlFor="cashback-enabled" className="text-lg font-semibold text-white mb-1 block cursor-pointer">
              Ativar Cashback
            </Label>
            <p className="text-sm text-gray-400">Permite que clientes acumulem e usem cashback</p>
          </div>
          <Switch
            id="cashback-enabled"
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings({...settings, enabled: checked})}
            data-testid="cashback-enabled-toggle"
          />
        </div>

        {/* Percentage Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Percentual de Cashback (%)
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={settings.percentage}
            onChange={(e) => setSettings({...settings, percentage: parseFloat(e.target.value) || 0})}
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors"
            placeholder="5.0"
            disabled={!settings.enabled}
            data-testid="cashback-percentage-input"
          />
          <p className="text-xs text-gray-500">
            Percentual do valor do pedido que será devolvido como cashback
          </p>
        </div>

        {/* Expiry Days Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Validade do Cashback (dias)
          </label>
          <input
            type="number"
            min="1"
            step="1"
            value={settings.expiry_days}
            onChange={(e) => setSettings({...settings, expiry_days: parseInt(e.target.value) || 30})}
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors"
            placeholder="30"
            disabled={!settings.enabled}
            data-testid="cashback-expiry-input"
          />
          <p className="text-xs text-gray-500">
            Número de dias até o cashback expirar
          </p>
        </div>

        {/* Minimum Order Value Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Valor Mínimo do Pedido (R$)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={settings.minimum_order_value}
            onChange={(e) => setSettings({...settings, minimum_order_value: parseFloat(e.target.value) || 0})}
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors"
            placeholder="0.00"
            disabled={!settings.enabled}
            data-testid="cashback-minimum-input"
          />
          <p className="text-xs text-gray-500">
            Valor mínimo do pedido para ganhar cashback (deixe 0 para sem mínimo)
          </p>
        </div>

        {/* Save Button */}
        <div className="pt-4">
          <button
            onClick={handleSave}
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-xl hover:from-amber-600 hover:to-yellow-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cashback-save-button"
          >
            {loading ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>

        {/* Info Box */}
        {settings.enabled && (
          <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <h4 className="text-sm font-semibold text-amber-400 mb-2">Como funciona:</h4>
            <ul className="text-xs text-gray-300 space-y-1">
              <li>• Clientes ganham {settings.percentage}% de cashback em cada pedido</li>
              <li>• O cashback expira após {settings.expiry_days} dias</li>
              {settings.minimum_order_value > 0 && (
                <li>• Pedido mínimo de R$ {settings.minimum_order_value.toFixed(2)} para ganhar cashback</li>
              )}
              <li>• Clientes podem usar o cashback em pedidos futuros</li>
              <li>• O cashback é identificado pelo telefone do cliente</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashbackContent;
